
import { Injectable, inject } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Router, RouterStateSnapshot } from "@angular/router";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { DirtyModalComponent } from "../components/dirty-modal/dirty-modal.component";
import { ObjectUtils } from "../utils/object-utils";

export interface IDirtyFormResult {
    isCancel: boolean;
    isSkipSave: boolean;
    isSave: boolean;
    routeTo: string;
    routeFrom: string;
}

@Injectable({
    providedIn: "root"
})
export class DirtyFormService {

    form: UntypedFormGroup;
    doneFn: (done: IDirtyFormResult) => void;
    header: string;
    content: string;
    saveFn: () => any | Promise<any> | void;
    dirtyngModelFn: () => boolean;
    popupOptions: NgbModalOptions = { size: "md",
        centered: false };
    ngModelValidationFn: () => boolean;
    ignoreRoutes: string[];

    private readonly router = inject(Router);
    private readonly ngbModalService = inject(NgbModal);
    private readonly $translate = inject(TranslateService);

    onDirty(
        form: UntypedFormGroup,
        saveAction: () => any | Promise<any> | void = null,
        doneAction: (done: IDirtyFormResult) => void = null,
        ignoreRoutes: string[] = [],
        header: string = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_HEADER"),
        content: string = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_TEXT")): void {
        this.form = form;
        this.doneFn = doneAction;
        this.saveFn = saveAction;
        this.ngModelValidationFn = () => form.valid;
        this.header = header;
        this.content = content;
        this.ignoreRoutes = ignoreRoutes;
    }

    onDirtyNgModel(
        isDirtyNgModel: () => boolean = null,
        saveAction: () => void = null,
        doneAction: (done: IDirtyFormResult) => void = null,
        ngModelValidationFn: () => boolean = null,
        header = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_HEADER"),
        content = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_TEXT"),
        popupOptions: NgbModalOptions = null): void {
        this.dirtyngModelFn = isDirtyNgModel;
        this.doneFn = doneAction;
        this.saveFn = saveAction;
        this.header = header;
        this.content = content;
        this.ngModelValidationFn = ngModelValidationFn;
        if (popupOptions) {
            this.popupOptions = popupOptions;
        }
    }

    canClose(): boolean {
        return this.tryExit(null);
    }

    dirtyFormModal(
        skipSave: () => void, save: () => void,
        cancelFn: (arg: any) => void,
        header = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_HEADER"),
        content = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_TEXT")) {
        const options: NgbModalOptions = { size: "md",
            centered: false };
        const modalRef = this.ngbModalService.open(DirtyModalComponent, options);
        const componentInstance = (modalRef.componentInstance as DirtyModalComponent);
        componentInstance.header = header;
        componentInstance.content = content;
        componentInstance.showSave = save ? true : false;
        modalRef.result.then(modalResult => {
            const response = {
                isCancel: modalResult === "cancel",
                isSkipSave: modalResult === "ok",
                isSave: modalResult === "save",
                routeTo: ""
            };
            if (response.isSkipSave) {
                skipSave();
            } else if (response.isSave) {
                save();
            } else if (cancelFn != null) {
                cancelFn(modalResult);
            } else {
                console.error("DirtyFormService: no response- or save callback is configured.");
            }
        }, modalError => {
            cancelFn({
                isCancel: true,
                isSkipSave: false,
                isSave: false,
                routeTo: ""
            });
        });
    }

    dirtyFormModalAsync(
        showSave = true,
        header = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_HEADER"),
        content = this.$translate.instant("NAVIGATION_UNSAVEDDATA_PROMPT_TEXT")): Promise<any> {
        const options: NgbModalOptions = { size: "md",
            centered: false };
        const modalRef = this.ngbModalService.open(DirtyModalComponent, options);
        const componentInstance = (modalRef.componentInstance as DirtyModalComponent);
        componentInstance.header = header;
        componentInstance.content = content;
        componentInstance.showSave = showSave;
        return modalRef.result;
    }

    tryExit(currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot): boolean {
        const routeTo = nextState?.url ?? "";
        if (this.ignoreRoute(routeTo)) {
            return true;
        } else if (!this.hasUnsavedData()) {
            this.clearOldState();
            return true;
        } else {
            this.popupModal(currentState, nextState);
            return false;
        }
    }

    popDirtyDialog(skipSave: () => Promise<any>, onComplete: () => any) {
        if (!this.hasUnsavedData()) {
            onComplete();
            return;
        }

        const runAfterFn = (fn: any, callback: () => any) => {
            const res = fn();
            if (res?.then) {
                res.then(callback);
            } else {
                callback();
            }
        };

        this.innerPopupModal(modalResult => {
            const response = {
                isCancel: modalResult === "cancel",
                isSave: modalResult === "save",
                isSkipSave: modalResult === "ok",
                routeTo: "",
                routeFrom: ""
            };

            if (response.isSkipSave) {
                runAfterFn(skipSave, onComplete);
            } else if (this.ngModelValidationFn && response.isSave && this.saveFn != null) {
                if (this.ngModelValidationFn()) {
                    runAfterFn(this.saveFn, onComplete);
                }
            } else if (response.isSave && this.saveFn != null) {
                runAfterFn(this.saveFn, onComplete);
            } else if (this.doneFn != null) {
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                runAfterFn(() => {}, () => { });
            }
        }, modalError => {
            this.doneFn({
                isCancel: true,
                isSkipSave: false,
                isSave: false,
                routeTo: "",
                routeFrom: ""
            });
        });
    }

    private clearOldState() {
        this.dirtyngModelFn = null;
        this.form = null;
    }

    private popupModal(currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot) {
        this.innerPopupModal(modalResult => {
            const response: IDirtyFormResult = {
                isCancel: modalResult === "cancel",
                isSave: modalResult === "save",
                isSkipSave: modalResult === "ok",
                routeFrom: currentState?.url ?? null,
                routeTo: nextState?.url ?? null
            };

            if (response.isSkipSave) {
                this.navigate(response);
            } else if (this.ngModelValidationFn && response.isSave && this.saveFn != null) {
                if (this.ngModelValidationFn()) {
                    this.saveFn();
                    this.navigate(response);
                }
            } else if (response.isSave && this.saveFn != null) {
                this.navigate(response);
            } else if (this.doneFn != null) {
                this.doneFn(response);
            } else {
                console.error("DirtyFormService: no response- or save callback is configured.");
            }
        }, modalError => {
            this.doneFn({
                isCancel: true,
                isSkipSave: false,
                isSave: false,
                routeTo: nextState.url ?? "",
                routeFrom: currentState.url ?? ""
            });
        });
    }

    private innerPopupModal(onFulfill: (response) => any, reject: (reason) => any) {
        const options: NgbModalOptions = this.popupOptions;
        const modalRef = this.ngbModalService.open(DirtyModalComponent, options);
        const componentInstance = (modalRef.componentInstance as DirtyModalComponent);
        componentInstance.header = this.header;
        componentInstance.content = this.content;
        componentInstance.showSave = this.saveFn != null;
        modalRef.result.then(onFulfill, reject);
    }

    private navigate(r: IDirtyFormResult) {
        this.clearOldState();
        if (ObjectUtils.hasValue(r.routeTo)) {
            this.router.navigate([r.routeTo], {
                state: {
                    dirtyResolved: true
                }
            });
        } else if (this.doneFn != null) {
            this.doneFn(r);
        }
    }

    private hasUnsavedData(): boolean {
        if (this.form) {
            return this.form.dirty;
        }
        if (this.dirtyngModelFn) {
            return this.dirtyngModelFn();
        }
        return false;
    }

    private ignoreRoute(route: string) {
        if (this.ignoreRoutes?.length > 0) {
            return this.ignoreRoutes.includes(route);
        }

        return false;
    }
}
